import React, {useState, forwardRef, useImperativeHandle, useMemo, useEffect} from "react";
import {Accordion, AccordionDetails, AccordionSummary, CircularProgress, styled} from "@mui/material";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {TextInput, Label, FormItemContainer, Button} from "../../components";
import {
  Dialog,
} from "@mui/material";
import { debounce } from 'radash';
import PayToImage from '../../resource/figma-images/PayTo_wordmark-Black.png';
import PayToSuccessImage from '../../resource/figma-images/payto-success.png';
import PayToWarningImage from '../../resource/figma-images/payto-warning.png';
import {getThemeColor} from "../../customHostConfigOnClient";
import {useLocation} from "react-router-dom";

const PayIdWrapper = styled("div")`
  margin: 10px 0 20px;
`;

const ValidationLabel = styled("div")(({ success }) => ({
  marginTop: "5px",
  fontSize: "13px",
  color: success ? "green" : "red",
}));

const Selector = styled("select")`
  flex: 0 0 25%;
  min-width: 0;
  padding: 10px;
  border-radius: 10px;
  border: 1px solid #ccc;
  margin-right: 5px;
  font-size: 15px;
  font-family: inherit;
  line-height: normal;
  text-overflow: ellipsis;
  background-color: #fff;
  /* Add custom Chrome-like arrow */
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6' fill='none'><path d='M1 1L5 5L9 1' stroke='%23555' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/></svg>");
  background-repeat: no-repeat;
  background-position: right 10px center;
  background-size: 10px auto;

  /* Remove native arrow */
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  color: #000;
  height: 39px;

  /* Remove unexpected inner shadows on iOS */
  box-shadow: none;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px #007aff33; /* optional focus ring */
  }
`;

const PaymentResultWrapper = styled("div")`
  display: flex;
  background-color: #F9F7FC;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-height: 80px;
  width: 100%;
  max-width: 400px;
  padding: 20px 20px 30px;
`;

const InputWithPrefix = styled("div")`
  display: flex;
  align-items: center;
  border: 1px solid #ccc;
  border-radius: 10px;
  padding: 0 10px;
  background: #fff;
  width: 100%;
  min-width: 0;

  input {
    border: none;
    padding: 10px;
    font-size: 15px;
    outline: none;
    min-width: 0;
    width: 100%;
  }
`;

export const PayIDContainer = styled('div')`
  display: flex;
  flex-direction: row;
  align-items: center;
  flex-grow: 1;
  flex-basis: auto;
`;

const PayResultLogo = styled("div")`
  margin: 0 0 20px;
`

const PayResultTitle = styled("div")`
  font-weight: bold;
  margin-bottom: 15px;
`

const PayResultText = styled("div")`
  font-weight: normal;
  margin-bottom: 30px;
`

const AccordionWrapper = styled("div")`
  padding: 20px 0;
  display: flex;
  flex-direction: column;
`

const MuiAccordion = styled((props) => (
  <Accordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  marginBottom: '10px',
  borderRadius: '10px',
  '&::before': {
    display: 'none',
  },
}));

const MuiAccordionDetails = styled((props) => (
  <AccordionDetails {...props} />
))(({ theme }) => ({
  textAlign: 'left',
  fontSize: '13px',
}));


const getIdentifierValue = (type, value) => {
  if (type === "alias_phone") {
    const normalizedPhone = value.startsWith("0") ? value.slice(1) : value;
    return `+61-${normalizedPhone}`;
  }
  return value;
}

const getInputPlaceholder = (identifierType) => {
  switch (identifierType) {
    case "alias_phone":
      return "412345678";
    case "alias_email":
      return "name@example.com.au";
    case "alias_abn":
      return "";
    case "bban":
      return "123456-98765432";
    default:
      return "";
  }
}

const isValidBBAN = (bsb, accountNumber) => {
  const bsbValid = /^\d{6}$/.test(bsb);
  const accValid = /^\d{6,9}$/.test(accountNumber);
  return bsbValid && accValid;
};

const ResultDivWrapper = styled("div")`
  padding-left: 15%;
  align-self: flex-start;
`

const maskName = (name) => {
  if (!name) return "";
  return name
    .replace(/\s+/g, ' ') // collapse multiple spaces
    .trim()
    .split(" ")
    .map((part) => {
      if (part.length === 0) return "";
      if (part.length === 1) return part[0];
      return part[0] + part[1] + "*".repeat(Math.max(0, part.length - 2));
    })
    .join(" ");
};

const getBillAmountInCents = (amount) => Math.round(parseFloat(amount) * 100);

function isValidEmail(email) {
  // Simple email regex
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function isValidAuMobile(phone) {
  // Matches +61-4 followed by 8 digits
  return /^\+61-4\d{8}$/.test(phone);
}

function getValidationErrorMessage(type) {
  switch (type) {
    case "alias_phone":
      return "No PayID linked to this phone number";
    case "alias_email":
      return "No PayID linked to this email address";
    case "alias_organisation_identifier":
      return "No PayID linked to this Organisation ID";
    case "alias_abn":
      return "No PayID linked to this ABN or ACN";
    case "bban":
      return "No PayID linked to this BSB & Account Number";
    default:
      return '';
  }
}

export const PayIdInlineForm = forwardRef(({ onSubmitResult, billAmount, payIdMetadata }, ref) => {
  const [partyName, setPartyName] = useState("");
  const [identifierType, setIdentifierType] = useState("alias_phone");
  const [identifierValue, setIdentifierValue] = useState("");
  const [bsb, setBsb] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [validationResult, setValidationResult] = useState(null);
  const [processingProgress, setProcessingProgress] = useState("");
  const [isValid, setIsValid] = useState(false);

  // Dialog state
  const [submitting, setSubmitting] = useState(false);
  const [transactionStatus, setTransactionStatus] = useState(null); // "success" | "error" | null
  const [transactionMessage, setTransactionMessage] = useState("");

  const envParam = new URLSearchParams(useLocation().search).get('env');

  // Update identifierValue when bban changes
  useEffect(() => {
    if (identifierType === "bban") {
      setIdentifierValue(`${bsb}-${accountNumber}`);
      if (isValidBBAN(bsb, accountNumber)) {
        setIsValid(true);
        setValidationResult(null);
      } else {
        setIsValid(false);
      }
    }
  }, [bsb, accountNumber, identifierType]);

  const validateAlias = async (type, value) => {
    setValidationResult(null);
    setIsValid(false);

    const currentIdentifierValue = getIdentifierValue(type, value);

    if (type === "alias_email" && !isValidEmail(currentIdentifierValue)) {
      setValidationResult("Invalid email format");
      return false;
    }
    if (type === "alias_phone" && !isValidAuMobile(currentIdentifierValue)) {
      setValidationResult("Invalid Australian mobile number");
      return false;
    }

    try {
      const resp = await fetch(`/api/payid/alias-resolution?env=${envParam}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          identifierType: type,
          identifierValue: currentIdentifierValue,
        }),
      });

      const data = await resp.json();
      if (data?.display_name) {
        const masked = maskName(data.display_name);
        setValidationResult(`Valid PayID for ${masked}`);
        setIsValid(true);
        return true;
      } else {
        setValidationResult(getValidationErrorMessage(type));
        setIsValid(false);
        return false;
      }
    } catch (err) {
      setValidationResult("Validation request failed");
      setIsValid(false);
      return false;
    }
  };

  const handleInputChange = (val) => {
    setIdentifierValue(val);
    setValidationResult(null);
    setIsValid(false);
    if (identifierType !== "bban") {
      debouncedValidateAlias(identifierType, val);
    }
  };

  const debouncedValidateAlias = useMemo(
    () =>
      debounce({ delay: 500 }, async (type, value) => {
        if (!value) return;
        await validateAlias(type, value);
      }),
    []
  );

  const handleSelectorChange = (v) => {
    setIdentifierType(v);
    setIdentifierValue("")
    setValidationResult(null);
    setIsValid(false);
  };

  // ---------- Submission Flow (createAgreement → status → createPayment) ----------
  const submitPaymentFlow = async () => {
    if (identifierType === "bban" && isValidBBAN(bsb, accountNumber)) {
      setIsValid(true);
      setValidationResult(null);
    }

    if (!partyName || partyName.trim() === "") {
      setValidationResult("Please enter your Account Name!");
      return;
    }
    if (!isValid) {
      setValidationResult("Invalid PayID details, please check your input!");
      return;
    }

    setSubmitting(true);
    setTransactionStatus(null);
    setTransactionMessage("");

    try {
      // STEP 1: Create Agreement
      setProcessingProgress("Creating Agreement.");
      const resp = await fetch(`/api/payid/create-agreement?env=${envParam}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          partyName,
          identifierType,
          identifierValue: getIdentifierValue(identifierType, identifierValue),
          billAmount: getBillAmountInCents(billAmount), // in cents
        }),
      });
      const { agreementId } = await resp.json();

      setProcessingProgress("Agreement created. |Waiting Agreement to be Active. Please check your PayID App to approve it.");
      // Poll until agreement is successful
      let agreementOk = false;
      while (!agreementOk) {
        const s = await fetch(`/api/payid/agreement-status/${agreementId}?env=${envParam}`);
        const data = await s.json();
        if (data.state === "active") {
          agreementOk = true;
        } else if (data.state !== "pending" && data.state !== "created") {
          setTransactionStatus("error");
          setTransactionMessage("Agreement failed");
          onSubmitResult?.({ success: false, stage: "agreement", error: data });
          return;
        } else {
          await new Promise((r) => setTimeout(r, 2000));
        }
      }

      setProcessingProgress("Agreement created. |Agreement is Active. |Creating Payment...");
      // STEP 2: Create Payment
      const paymentResp = await fetch(`/api/payid/create-payment?env=${envParam}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customMetadata: payIdMetadata || {},
          billAmount: getBillAmountInCents(billAmount), // in cents
          agreementUid: agreementId
        }),
      });
      const payment = await paymentResp.json();

      // Poll until payment is successful
      setProcessingProgress("Agreement created. |Agreement is Active. |Waiting Payment to be Settled...");
      let paymentOk = false;
      while (!paymentOk) {
        const s = await fetch(`/api/payid/payment-status/${payment.paymentId}?env=${envParam}`);
        const data = await s.json();
        if (data.state === "settled") {
          paymentOk = true;
        } else if (data.state !== "created" && data.state !== "submitting") {
          setTransactionStatus("error");
          setTransactionMessage("Payment failed");
          onSubmitResult?.({ success: false, stage: "payment", error: data });
          return;
        } else {
          await new Promise((r) => setTimeout(r, 2000));
        }
      }

      setTransactionStatus("success");
      setTransactionMessage("Payment Successful");
      onSubmitResult?.({ success: true, stage: "payment", data: payment });

    } catch (err) {
      setTransactionStatus("error");
      setTransactionMessage("Error submitting PayID payment");
      onSubmitResult?.({ success: false, stage: "exception", error: err });
    }
  };

  // Expose submit() to parent via ref
  useImperativeHandle(ref, () => ({
    submit: submitPaymentFlow,
  }));

  const renderIdentifierInputs = (type) => {
    switch (type) {
      case 'bban':
        return (<div style={{ display: 'flex', gap: '5px', flexGrow: 1}}>
          <TextInput
            placeholder="BSB"
            value={bsb}
            onChange={setBsb}
            maxLength={6}
            $flexBasis={'35%'}
          />
          <TextInput
            placeholder="Account Number"
            value={accountNumber}
            onChange={setAccountNumber}
            maxLength={9}
            $flexBasis={'65%'}
          />
        </div>);
      case 'alias_phone':
        return (
          <InputWithPrefix>
            <span>+61</span>
            <input
              placeholder={getInputPlaceholder(identifierType)}
              value={identifierValue}
              onChange={(e) => handleInputChange(e.target.value)}
            />
          </InputWithPrefix>
        );
      default:
        return (
          <TextInput
            placeholder={getInputPlaceholder(identifierType)}
            value={identifierValue}
            onChange={handleInputChange}
          />
        );
    }
  }

  return (
    <PayIdWrapper>
      <FormItemContainer>
        <Label isRequired>Account Name</Label>
        <TextInput
          placeholder="Enter account name, e.g: John Smith"
          value={partyName || ''}
          onChange={setPartyName}
        />
      </FormItemContainer>
      <div style={{ marginTop: 20, textAlign: 'left' }}>
        <Label>PayID Type</Label>
      </div>
      <PayIDContainer>
        <Selector
          value={identifierType}
          onChange={(e) => handleSelectorChange(e.target.value)}
        >
          <option value="alias_phone">Phone</option>
          <option value="alias_email">Email</option>
          <option value="alias_organisation_identifier">Org ID</option>
          <option value="alias_abn">ABN or ACN</option>
          <option value="bban">BSB & Account Number</option>
        </Selector>
        { renderIdentifierInputs(identifierType) }
      </PayIDContainer>
      {validationResult && (
        <ValidationLabel success={isValid}>{validationResult}</ValidationLabel>
      )}

      {/* Dialog Popup */}
      <Dialog open={submitting} onClose={() => {}}>
        <PaymentResultWrapper>
          {transactionStatus === null && (
            <>
              <PayResultLogo style={{marginTop: "15px"}}>
                <img src={PayToImage} width='100px' />
              </PayResultLogo>
              <PayResultTitle>
                Authorise Your PayTo Agreement
              </PayResultTitle>
              <PayResultText>
                Open your mobile banking app or online banking to authorise and pay with PayTo.
              </PayResultText>
              <CircularProgress />
              <AccordionWrapper>
                <MuiAccordion>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1-content"
                    id="panel1-header"
                  >
                    <span>How to authorise</span>
                  </AccordionSummary>
                  <MuiAccordionDetails>
                    <p>
                      1. Login to your mobile or internet banking.
                    </p>
                    <p>
                      2. Review the PayTo agreement.
                    </p>
                    <p>
                      3. Authorise the agreement.
                    </p>
                    <p>
                      4. Your order will be processed.
                    </p>
                  </MuiAccordionDetails>
                </MuiAccordion>
                <MuiAccordion>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel2-content"
                    id="panel2-header"
                  >
                    <span>Payment Agreement details</span>
                  </AccordionSummary>
                  <MuiAccordionDetails>
                    <div><strong>Account Name:</strong> {partyName}</div>
                    <div><strong>PayID Identifier:</strong> {identifierValue}</div>
                    <div><strong>Amount:</strong> ${billAmount}</div>
                  </MuiAccordionDetails>
                </MuiAccordion>
              </AccordionWrapper>

              <div style={{ fontSize: '13px', color: '#ACAAAD' }}>
                Please do not close this window while we retrieve your PayTo Agreement authorisation status.
                Once your PayTo Agreement has been authorised, payment status will be updated automatically.
              </div>
            </>
          )}
          {transactionStatus === "success" && (
            <>
              <PayResultLogo style={{marginTop: "15px"}}>
                <img src={PayToSuccessImage} width='120px' />
              </PayResultLogo>
              <PayResultTitle>
                Payment Successful
              </PayResultTitle>
              <PayResultText>
                Your payment has been authorised and completed successfully.
              </PayResultText>

              <ResultDivWrapper><strong>Account Name:</strong> {partyName}</ResultDivWrapper>
              <ResultDivWrapper><strong>PayID Identifier:</strong> {identifierValue}</ResultDivWrapper>
              <ResultDivWrapper><strong>Amount:</strong> ${billAmount}</ResultDivWrapper>

              <Button $fontSize='16px' $padding='10px' $margin='20px 0 0' $themeColor={getThemeColor()} onClick={() => setSubmitting(false)} block>
                Close
              </Button>
            </>
          )}
          {transactionStatus === "error" && (
            <>
              <PayResultLogo style={{marginTop: "15px"}}>
                <img src={PayToWarningImage} width='120px' />
              </PayResultLogo>
              <PayResultTitle>
                PayTo Agreement Not Added
              </PayResultTitle>
              <PayResultText>
                Your PayTo Agreement authorisation request was not completed. Please try again.
              </PayResultText>

              <ResultDivWrapper><strong>Account Name:</strong> {partyName}</ResultDivWrapper>
              <ResultDivWrapper><strong>PayID Identifier:</strong> {identifierValue}</ResultDivWrapper>
              <ResultDivWrapper><strong>Amount:</strong> ${billAmount}</ResultDivWrapper>

              <Button $fontSize='16px' $padding='10px' $margin='20px 0 0' $themeColor={getThemeColor()} onClick={() => setSubmitting(false)} block>
                Cancel
              </Button>
            </>
          )}
        </PaymentResultWrapper>
      </Dialog>
    </PayIdWrapper>
  );
});
